import type Product from "@/models/Product";

class GoogleLdJsonService {
  private cleanText(value?: string | null, maxLength?: number) {
    if (!value) {
      return undefined;
    }

    const cleanValue = value.replace(/\s+/g, " ").trim();
    if (!cleanValue) {
      return undefined;
    }

    return typeof maxLength === "number"
      ? cleanValue.substring(0, maxLength)
      : cleanValue;
  }

  private toAbsoluteUrl(url?: string | null, baseUrl?: string) {
    if (!url) {
      return undefined;
    }

    try {
      return new URL(url, baseUrl).toString();
    } catch {
      return url;
    }
  }

  public formatProduct(product: Product, extra: { productLink: string }) {
    const productImage = this.toAbsoluteUrl(
      product.getCoverImage(),
      extra.productLink,
    );
    const returnPolicyUrl = this.toAbsoluteUrl(
      "/politicas-de-devolucion",
      extra.productLink,
    );
    const json = {
      "@context": "https://schema.org",
      "@type": "Product",
      ...(this.cleanText(product.meta_description)
        ? { description: this.cleanText(product.meta_description) }
        : {}),
      name: this.cleanText(product.name, 150), // Google acepta máximo 150 aprox. en este campo
      ...(product.seo_descriptive_title
        ? {
            alternateName: this.cleanText(product.seo_descriptive_title, 150),
          }
        : {}),
      mpn: product.part_number,
      url: extra.productLink,
      ...(product.upc_gtin ? { gtin: product.upc_gtin } : {}),
      ...(product.part_number ? { sku: product.part_number } : {}),
      ...(productImage ? { image: productImage } : {}),
      ...(product.brand
        ? {
            brand: {
              "@type": "Brand",
              name: this.cleanText(product.brand.name),
            },
          }
        : {}),
      ...(product.category ? { category: product.category.name } : {}),
      offers: {
        "@type": "Offer",
        availability:
          "https://schema.org/" +
          (product.stock_total ? "InStock" : "OutOfStock"),
        priceCurrency: "MXN",
        ...(product.price_valid_until_utc
          ? { priceValidUntil: product.price_valid_until_utc }
          : {}),
        url: extra.productLink,
        ...(product.hasDiscount()
          ? {
              price: product.getFinalCurrentPrice().toFixed(2),
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                priceType: "https://schema.org/StrikethroughPrice",
                price: product.getFinalCommonPrice().toFixed(2),
                priceCurrency: "MXN",
              },
            }
          : {
              price: product.getFinalCurrentPrice().toFixed(2),
            }),
        // Datos recomendados de envío
        shippingDetails: {
          "@context": "https://schema.org",
          "@type": "OfferShippingDetails",
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "MX",
            addressRegion: "JAL",
            postalCodeRange: {
              "@type": "PostalCodeRangeSpecification",
              postalCodeBegin: "01000",
              postalCodeEnd: "99998",
            },
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            businessDays: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "https://schema.org/Monday",
                "https://schema.org/Tuesday",
                "https://schema.org/Wednesday",
                "https://schema.org/Thursday",
                "https://schema.org/Friday",
                "https://schema.org/Saturday",
                "https://schema.org/Sunday",
              ],
            },
            cutoffTime: "12:00:00Z",
            handlingTime: {
              "@type": "QuantitativeValue",
              minValue: 1,
              maxValue: 2,
              unitCode: "d",
            },
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: 3,
              maxValue: 5,
              unitCode: "d",
            },
          },
          shippingRate: {
            "@type": "MonetaryAmount",
            value: 150,
            currency: "MXN",
          },
        },
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          name: product.warranty_days + "-días para devolver",
          ...(returnPolicyUrl ? { url: returnPolicyUrl } : {}),
          merchantReturnDays: product.warranty_days,
          returnFees: "https://schema.org/FreeReturn",
          returnPolicyCategory: "MerchantReturnFiniteReturnWindow",
          applicableCountry: "MX",
          returnMethod: "ReturnInStore",
        },
      },
      // Reseñas
      ...// Agrega las reseñas existentes junto con la calificación general
      (product.reviews.length > 0 && product.rating >= 3
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: product.rating.toFixed(0),
              reviewCount: product.reviews_count.toString(),
              worstRating: "1",
              bestRating: "5",
            },
            review: product.reviews.map((review) => ({
              "@type": "Review",
              author: {
                "@type": "Person",
                name: review.customer_name,
              },
              ...(review.created_utc
                ? { datePublished: review.created_utc }
                : {}),
              reviewBody: review.opinion,
              name: review.short_opinion,
              reviewRating: {
                "@type": "Rating",
                ratingValue: review.rating.toString(),
                worstRating: "1",
                bestRating: "5",
              },
            })),
          }
        : // Si no hay reseñas, se pone una calificación default en 5 (es requerido por Google)
          {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "5",
              reviewCount: "1",
              worstRating: "1",
              bestRating: "5",
            },
            review: [
              {
                "@type": "Review",
                author: {
                  "@type": "Person",
                  name: "Digitalife",
                },
                ...(product.created_utc
                  ? { datePublished: product.created_utc }
                  : {}),
                reviewBody:
                  "Producto de calidad: garantizado y recomendado por Digitalife.",
                name: "Recomendado por Digitalife",
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: "5",
                  worstRating: "1",
                  bestRating: "5",
                },
              },
            ],
          }),
    };
    return json;
  }

  public formatBreadcrumbList(
    items: Array<{ name: string; item?: string | null }>,
  ) {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((breadcrumbItem, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: breadcrumbItem.name,
        ...(breadcrumbItem.item ? { item: breadcrumbItem.item } : {}),
      })),
    };
  }

  public formatScript(dataFormat: Record<string, unknown>) {
    return {
      type: "application/ld+json",
      innerHTML: JSON.stringify(dataFormat),
    };
  }

  public formatProductForUseHead(
    product: Product,
    extra: { productLink: string },
  ) {
    const data = this.formatProduct(product, extra);
    const format = this.formatScript(data);
    return format;
  }
}

const googleLdJsonService = new GoogleLdJsonService();

export default googleLdJsonService;
